/**
 * Spec 76 Phase C — useThreadSwap
 *
 * Resolves the active conversationId for ConversationDetail when a context
 * panel is open in 'editor' mode.
 *
 * - Panel closed OR mode === 'viewer' → returns { activeConvId: parentConvId }
 * - Panel open + mode === 'editor'    → find-or-create the entity's assistant
 *   conversation via POST /api/conversations, then return its id.
 *
 * The find-or-create query is idempotent per (userId, subjectType, subjectId,
 * entry point) on the server — calling it twice with the same params returns
 * the same row. staleTime = Infinity keeps it cached for the session.
 */
import { useQuery } from '@tanstack/react-query';
import { listConversationsQueryKey } from '../../generated/api/@tanstack/react-query.gen.js';
import { conversationsApi } from '../../services/api/conversations.api.js';
import { getPanelType } from './registry.js';
import type { PanelPointer } from './url-state.js';

// Mirrors the server's `CreateConversation.subjectType` enum
// (server/src/api/routes/conversations.ts). Editor-mode panel types MUST
// be a subset of this set; if a future panel type doesn't fit, expand the
// server enum and regenerate the SDK rather than widening this cast.
const SDK_SUBJECT_TYPES = [
  'daemon-draft',
  'workflow',
  'template',
  'tool',
  'project',
  'project_thread',
  'project_change',
] as const;
type SdkSubjectType = (typeof SDK_SUBJECT_TYPES)[number];

function isSdkSubjectType(value: string): value is SdkSubjectType {
  return (SDK_SUBJECT_TYPES as readonly string[]).includes(value);
}

// ─── Return type ─────────────────────────────────────────────────────────────

export type ThreadSwapStatus =
  | { status: 'parent'; activeConvId: string }
  | { status: 'pending'; activeConvId: null }
  | { status: 'child'; activeConvId: string }
  | { status: 'error'; activeConvId: null; errorMessage: string };

// ─── Hook ─────────────────────────────────────────────────────────────────────

/** The panel's thread is a child conversation of the parent: keyed under the parent's conversation list. */
export function threadSwapQueryKey(parentConvId: string, type: string, id: string) {
  return [...listConversationsQueryKey({ query: { parentConversationId: parentConvId } }), type, id];
}

export function useThreadSwap(parentConvId: string, panel: PanelPointer | null): ThreadSwapStatus {
  const definition = panel !== null ? getPanelType(panel.type) : null;

  // Only run the find-or-create query when panel is open AND type is 'editor'.
  const isEditorMode = panel !== null && definition !== null && definition.mode === 'editor';

  const {
    data: entityConv,
    isPending,
    isError,
    error,
  } = useQuery({
    queryKey: threadSwapQueryKey(parentConvId, panel?.type ?? '', panel?.id ?? ''),
    queryFn: async () => {
      if (!panel || !definition?.entryPoint) {
        throw new Error('Panel type missing entryPoint for editor mode');
      }

      // Reject panel types that aren't in the SDK's subjectType enum BEFORE
      // calling the API. Without this guard, an unknown type would be sent
      // to the server and rejected with a 422; we'd rather fail-fast here
      // with a clear message that a panel type was registered but the SDK
      // hasn't been widened to accept it.
      if (!isSdkSubjectType(panel.type)) {
        throw new Error(
          `Panel type "${panel.type}" is not a server-known subjectType. ` +
            `Add it to CreateConversation.subjectType in conversations.ts and regenerate the SDK.`,
        );
      }

      const res = await conversationsApi.create({
        body: {
          topic: `Assistant: ${panel.type}`,
          kind: 'assistant-ui',
          entryPoint: definition.entryPoint,
          subjectType: panel.type,
          subjectId: panel.id,
          parentConversationId: parentConvId,
          contextSpec: {},
        },
      });

      const conv = (res as { data: { id: string | number } }).data;
      return { id: String(conv.id) };
    },
    enabled: isEditorMode,
    // Never re-fetch — same panel = same child conversation for this session.
    staleTime: Number.POSITIVE_INFINITY,
  });

  // Panel closed or viewer mode → use parent thread
  if (!isEditorMode) {
    return { status: 'parent', activeConvId: parentConvId };
  }

  // Editor mode: waiting for child thread
  if (isPending) {
    return { status: 'pending', activeConvId: null };
  }

  if (isError) {
    const msg = error instanceof Error ? error.message : 'Failed to open assistant thread';
    return { status: 'error', activeConvId: null, errorMessage: msg };
  }

  return { status: 'child', activeConvId: entityConv!.id };
}
